import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import $ from "jquery";
import "datatables.net-bs4";
import { db } from "../../index";
import { UserContext } from "../../App";
import {
  createInvoicesAndBookingsFromEstimateList,
  deleteEstimatesFromEstimateIdList,
  duplicateEstimateFromEstimateIdList,
  sendEstimateEmail,
  checkEstimatesExistOnStripe,
} from "../../store/actions/estimateActions";
import ActionsMenu from "../layout/ActionsMenu";
import { NavItem, NavTabs } from "../layout/Nav";
import { verifyRight } from "../../utils/users";
import { StyledTr } from "./bookingContent";
import SelectionActionsPopup from "../layout/SelectionActionsPopup";
import TrashIcon from "../icons/TrashIcon";
import CopyIcon from "../icons/CopyIcon";
import { OutlineButtonLink } from "../layout/OutlineButton";
import SelectContactsModal from "../../utils/estimate-invoice/SelectContactsModal";

export const estimateStatus = {
  draft: "Brouillon",
  open: "A envoyer",
  sent: "Envoyé",
  accepted: "Approuvé",
  canceled: "Annulé",
  paid: "Payé",
  invoiced: "Facture créée",
};

const estimateStatusOrder = {
  draft: 0,
  open: 1,
  accepted: 2,
  invoiced: 3,
  paid: 4,
  canceled: 5,
};

export default function EstimateContent() {
  const { userInfos, loading } = useContext(UserContext);
  const [estimateList, setEstimateList] = useState([]);

  const [selectedEstimates, setSelectedEstimates] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [currentEstimate, setCurrentEstimate] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const init = useRef(false);
  const datatable = useRef(null);

  useEffect(() => {
    if (estimateList.length > 0) {
      datatable.current = $("#estimate-datatable").DataTable({
        order: [[3, "desc"]],
        pageLength: 25,
        sortable: true,
      });

      datatable.current.draw();
    }
  }, [estimateList]);

  const initData = useCallback(() => {
    if (
      !loading &&
      userInfos &&
      userInfos.jwt &&
      userInfos.uid &&
      !init.current
    ) {
      // load estimates from the firebase db
      db.ref("/users/" + userInfos.uid + "/estimates/").once(
        "value",
        (snapshot) => {
          let data = snapshot.val();
          if (!data) data = [];
          setEstimateList(
            Object.keys(data).map((k) => {
              return {
                ...data[k],
                id: k,
              };
            })
          );
          init.current = true;
        }
      );
    }
  }, [loading, userInfos]);

  useEffect(() => {
    initData();
  }, [initData]);

  const duplicateEstimateListClick = useCallback(async () => {
    await duplicateEstimateFromEstimateIdList(selectedEstimates, userInfos.jwt);
    window.alert("Devis dupliqués avec succès");
    window.location.reload();
  }, [selectedEstimates, userInfos.jwt]);

  const deleteEstimateListClick = useCallback(async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ces devis ?"))
      return;
    await deleteEstimatesFromEstimateIdList(selectedEstimates, userInfos.jwt);
    window.alert("Devis supprimés avec succès");
    window.location.reload();
  }, [selectedEstimates, userInfos.jwt]);

  const toggleSelected = useCallback((id) => {
    setSelectedEstimates((_selectedEstimates) => {
      if (_selectedEstimates.indexOf(id) !== -1) {
        return _selectedEstimates.filter((e) => e !== id);
      } else {
        return [..._selectedEstimates, id];
      }
    });
  }, []);

  const sendEstimateEmailDirectly = useCallback(async (estimate, selectedContacts = []) => {
    setIsSending(true);
    try {
      await sendEstimateEmail(estimate.id, userInfos.jwt, selectedContacts);
      window.alert("Email envoyé avec succès");
      window.location.reload();
    } catch (error) {
      console.error("Error sending email:", error);
      const apiError =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.error ||
            error?.response?.data?.message ||
            "";
      if (!apiError) {
        window.alert("Erreur lors de l'envoi de l'email");
      }
    } finally {
      setIsSending(false);
    }
  }, [userInfos.jwt]);

  const handleSendEmail = useCallback(async (selectedContacts) => {
    if (!currentEstimate) return;

    await sendEstimateEmailDirectly(currentEstimate, selectedContacts);
    setShowContactsModal(false);
  }, [currentEstimate, sendEstimateEmailDirectly]);

  // Check if estimate exists on Stripe before action
  const checkEstimateBeforeAction = useCallback(async (estimateId, estimateStatus) => {
    if (estimateStatus === "draft") return true; // Draft estimates don't need Stripe check
    
    try {
      const response = await checkEstimatesExistOnStripe([estimateId], userInfos.jwt);
      const check = response.data[0];
      
      if (!check.existsOnStripe) {
        window.alert(`Stripe mode mismatch for estimate: ${estimateId}`);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking estimate on Stripe:", error);
      return true; // Allow action if check fails
    }
  }, [userInfos.jwt]);

  const tableBody = useMemo(() => {
    return estimateList.map((e) => {
      let actions = [];
      if (verifyRight(userInfos, "estimate", "update")) {
        actions.push({
          label: "Modifier",
          link: "/estimates/" + e.id + "/",
        });
      }

      if (e.status !== "draft" && e.status !== "invoiced")
        actions.push({
          label: "Page de paiement",
          externalLink: true,
          link: "/sign-estimate/" + userInfos.uid + "/" + e.token,
        });

      if (verifyRight(userInfos, "estimate", "create"))
        actions.push({
          label: "Dupliquer",
          onClick: async () => {
            await duplicateEstimateFromEstimateIdList([e.id], userInfos.jwt);
            window.alert("Devis dupliqué avec succès");
            window.location.reload();
          },
        });

      if (
        e.status === "draft" ||
        e.status === "open" ||
        e.status === "sent" ||
        e.status === "accepted"
      ) {
        if (verifyRight(userInfos, "estimate", "update"))
          actions.push({
            label: "Envoyer par email",
            onClick: () => {
              // Check if the customer has contacts before showing the modal
              db.ref(`users/${userInfos.uid}/provider/${e.customer.id}`)
                .once("value", (snapshot) => {
                  const providerData = snapshot.val();
                  if (providerData && providerData.contacts && providerData.contacts.length > 0) {
                    setCurrentEstimate(e);
                    setShowContactsModal(true);
                  } else {
                    // If no contacts, use the direct function that doesn't rely on state
                    sendEstimateEmailDirectly(e);
                  }
                });
            },
          });

        if (verifyRight(userInfos, "invoice", "create"))
          actions.push({
            label: "Créer facture (non payé) et les courses",
            onClick: async () => {
              try {
                await createInvoicesAndBookingsFromEstimateList(
                  [e.id],
                  userInfos.jwt
                );
                window.alert("Facture et courses créées avec succès");
                window.location.reload();
              } catch (error) {
                const apiError =
                  typeof error?.response?.data === "string"
                    ? error.response.data
                    : error?.response?.data?.error ||
                      error?.response?.data?.message ||
                      "";
                if (!apiError) {
                  window.alert(
                    "Erreur lors de la création de la facture et des courses"
                  );
                }
              }
            },
          });
      }

      actions.push({ divider: true });

      if (e.status === "draft") {
        if (verifyRight(userInfos, "estimate", "delete"))
          actions.push({
            label: "Delete",
            onClick: async () => {
              if (
                !window.confirm("Etes-vous sûr de vouloir supprimer ce devis ?")
              )
                return;
              await deleteEstimatesFromEstimateIdList([e.id], userInfos.jwt);
              window.alert("Devis supprimé avec succès");
              window.location.reload();
            },
          });
      }

      actions.push({ divider: true });
      if (e.invoice) {
        if (verifyRight(userInfos, "invoice", "read"))
          actions.push({
            label: "Voir la facture",
            link: "/invoices/" + e.invoice,
          });
      }

      return (
        <StyledTr
          key={e.id}
          className={selectedEstimates.indexOf(e.id) !== -1 ? "checked" : ""}
        >
          <td>
            <input
              type="checkbox"
              checked={selectedEstimates.indexOf(e.id) !== -1}
              onChange={() => toggleSelected(e.id)}
            />
          </td>
          <td>{e.id}</td>
          <td>{e.customer.firstname + " " + e.customer.lastname}</td>
          <td data-order={e.createdAt}>
            {moment((e.createdAt || 0) * 1000).format("DD MMM YYYY")}
          </td>
          <td data-order={estimateStatusOrder[e.status]}>
            {estimateStatus[e.status]}
          </td>
          <td>{parseFloat(e.price).toFixed(2)} €</td>
          <td>{e.bookingsCreated ? "Yes" : "No"}</td>
          <td>
            <ActionsMenu 
              items={actions} 
              onBeforeOpen={() => checkEstimateBeforeAction(e.id, e.status)}
            />
          </td>
        </StyledTr>
      );
    });
  }, [estimateList, selectedEstimates, sendEstimateEmailDirectly, toggleSelected, userInfos, checkEstimateBeforeAction]);

  const Footer = useMemo(() => {
    if (selectedEstimates.length === 0) return null;
    let actions = [];

    if (verifyRight(userInfos, "estimate", "create")) {
      actions.push({
        label: <CopyIcon />,
        title: "Dupliquer",
        onClick: duplicateEstimateListClick,
      });
    }

    if (verifyRight(userInfos, "estimate", "delete")) {
      actions.push({
        label: <TrashIcon />,
        title: "Supprimer",
        onClick: deleteEstimateListClick,
      });
    }

    return (
      <SelectionActionsPopup
        buttons={actions}
        items={[]}
        resetChecked={() => setSelectedEstimates([])}
        selectedItemsLength={selectedEstimates.length}
      />
    );
  }, [
    deleteEstimateListClick,
    duplicateEstimateListClick,
    selectedEstimates.length,
    userInfos,
  ]);

  if (!userInfos) return null;

  return (
    <div className="body-container" id="kt_content">
      <div className="title-container">
        <NavTabs className="titles">
          {verifyRight(userInfos, "estimate", "read") && (
            <NavItem className="active">
              <Link to="/estimates">Devis</Link>
            </NavItem>
          )}
          {verifyRight(userInfos, "invoice", "read") && (
            <NavItem>
              <Link to="/invoices">Factures</Link>
            </NavItem>
          )}
        </NavTabs>
        <div className="header-toolbar">
          {verifyRight(userInfos, "estimate", "create") && (
            <OutlineButtonLink to="/addEstimate/">
              <i className="la la-plus"></i>
              Nouveau devis
            </OutlineButtonLink>
          )}
        </div>
      </div>
      <div className="kt-portlet__body" style={{ overflowX: "scroll" }}>
        <div style={{ overflowX: "scroll" }}>
          <table
            className="table table-striped- table-bordered table-hover table-checkable"
            id="estimate-datatable"
          >
            <thead>
              <tr>
                <th style={{ width: "1%" }}></th>
                <th style={{ width: "5%" }}>ID</th>
                <th style={{ width: "15%" }}>Client</th>
                <th style={{ width: "5%" }}>Date</th>
                <th style={{ width: "5%" }}>Statut</th>
                <th style={{ width: "5%" }}>Total TTC</th>
                <th style={{ width: "5%" }}>Courses créées ?</th>
                <th style={{ width: "2%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>{tableBody}</tbody>
          </table>
        </div>
        {Footer}
      </div>

      <SelectContactsModal
        show={showContactsModal}
        setShow={setShowContactsModal}
        estimate={currentEstimate}
        submit={handleSendEmail}
        fetching={isSending}
      />
    </div>
  );
}
